import clsx from 'clsx'
import { useEffect, useRef, useState, type RefObject } from 'react'

import { CollapseAlt, ExpandAlignRight } from '@/assets/icons'
import AgentV2ConversationDialog from '@/agent-v2/conversations/components/AgentV2ConversationDialog'
import AgentV2ConversationMenu from '@/agent-v2/conversations/components/AgentV2ConversationMenu'
import { selectAgentV2ConversationFromId } from '@/agent-v2/conversations/store/selectors'
import { IconButton } from '@/components/icon-button/IconButton'
import TruncatedText from '@/components/truncated-text/TruncatedText'
import { kTooltipHoverDelayMs } from '@/constants/constants-ui'
import type { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { useChatPanelTabs } from '../hooks/useChatPanelTabs'
import { useDraftWorkspace } from '../hooks/useDraftWorkspace'
import { ChatPanelTabType, DraftWorkspaceActions } from '../store/slice'
import ChatPanelTabBar from './ChatPanelTabBar'
import ChatPanelTabList from './ChatPanelTabList'
import ChatViewMenu from './ChatViewMenu'

type ChatPanelHeaderProps = {
  conversationId: string
  /* Focused when the panel is reopened, since the button that reopened it unmounts. */
  collapseButtonRef?: RefObject<HTMLButtonElement>
}

const kConversationHeaderClassName = 'flex h-12 shrink-0 items-center gap-1 border-b border-border-default bg-surface-content'

/**
 * Header for the chat side panel in the drafting workspace.
 *
 * With a source open, the header grows a tab bar and the conversation's own header drops beneath it.
 */
export default function ChatPanelHeader({ conversationId, collapseButtonRef }: ChatPanelHeaderProps) {
  const dispatch = useAppDispatch()
  const { draftArtifactVersionId } = useDraftWorkspace()
  const { sourceSession, selectedTab } = useChatPanelTabs()
  const hasSourceTab = sourceSession !== null
  const hadSourceTab = useRef(hasSourceTab)

  // Closing the source takes the tab bar with it, including the close control that was focused. Only when focus
  // was left stranded, so a source cleared while the reader is elsewhere leaves them where they are.
  useEffect(() => {
    const sourceTabClosed = hadSourceTab.current && !hasSourceTab
    hadSourceTab.current = hasSourceTab
    if (!sourceTabClosed || document.activeElement !== document.body) return
    collapseButtonRef?.current?.focus()
  }, [collapseButtonRef, hasSourceTab])

  const collapse = () => {
    if (draftArtifactVersionId) dispatch(DraftWorkspaceActions.setChatPanelOpen({ draftArtifactVersionId, open: false }))
  }

  const collapseButtonProps = {
    ref: collapseButtonRef,
    'aria-label': 'Minimize chat',
    tooltipText: 'Minimize',
    tooltipPlacement: 'bottom',
    tooltipHoverDelayMs: kTooltipHoverDelayMs,
    disableTooltipInteractive: true,
    disableTooltipFocusListener: true,
    onClick: collapse,
  } as const

  if (!sourceSession || !draftArtifactVersionId) {
    return <ChatConversationHeader conversationId={conversationId} onCollapse={collapse} collapseButtonRef={collapseButtonRef} />
  }

  return (
    <>
      <ChatPanelTabBar>
        <IconButton variant="tonal" size="sm" icon={<CollapseAlt />} {...collapseButtonProps} />
        <ChatPanelTabList
          conversationId={conversationId}
          draftArtifactVersionId={draftArtifactVersionId}
          sourceSession={sourceSession}
          selectedTab={selectedTab}
        />
      </ChatPanelTabBar>
      {/* The source's own sub-header comes from the viewer it renders. */}
      {selectedTab === ChatPanelTabType.Assistant && (
        <div className={clsx(kConversationHeaderClassName, 'pl-3 pr-2')}>
          <ConversationControls conversationId={conversationId} />
        </div>
      )}
    </>
  )
}

/** Conversation header for a docked draft. */
function ChatConversationHeader({
  conversationId,
  onCollapse,
  collapseButtonRef,
}: {
  conversationId: string
  onCollapse: () => void
  collapseButtonRef?: RefObject<HTMLButtonElement>
}) {
  return (
    <div className={clsx(kConversationHeaderClassName, 'px-2')}>
      <IconButton
        ref={collapseButtonRef}
        variant="plain"
        color="neutral"
        size="md"
        icon={<ExpandAlignRight />}
        aria-label="Minimize chat"
        tooltipText="Minimize"
        tooltipPlacement="bottom"
        tooltipHoverDelayMs={kTooltipHoverDelayMs}
        disableTooltipInteractive
        disableTooltipFocusListener
        onClick={onCollapse}
      />
      <ConversationControls conversationId={conversationId} />
    </div>
  )
}

/** The open conversation's title and its view and rename/download/delete menus. */
function ConversationControls({ conversationId }: { conversationId: string }) {
  const conversation = useAppSelector((state: RootState) => selectAgentV2ConversationFromId(state, { conversationId }))
  const title = conversation?.title || 'AI Assistant'
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  return (
    <>
      <div className="flex-1 min-w-0">
        <TruncatedText placement="bottom-start" className="cursor-default text-sm font-semibold text-text-primary">
          {title}
        </TruncatedText>
      </div>
      <ChatViewMenu conversationId={conversationId} />
      <AgentV2ConversationMenu
        conversationId={conversationId}
        iconSize="md"
        triggerAriaLabel="More chat actions"
        tooltipText="More"
        onSelectRename={() => setRenameOpen(true)}
        onSelectDelete={() => setDeleteOpen(true)}
      />
      <AgentV2ConversationDialog conversationId={conversationId} kind="rename" open={renameOpen} onClose={() => setRenameOpen(false)} />
      <AgentV2ConversationDialog conversationId={conversationId} kind="delete" open={deleteOpen} onClose={() => setDeleteOpen(false)} />
    </>
  )
}
